/**
 * Typed API client for the Opportunity Snapshot (epic 126). The snapshot scores
 * an account/opportunity/lead across weighted signal categories; generation is
 * gated on the `agents` module (a 403 surfaces as a module-required state).
 */

import { apiClient } from '@/lib/apiClient';
import { isAxiosError } from 'axios';

export type SnapshotEntityType = 'ACCOUNT' | 'OPPORTUNITY' | 'LEAD';

export interface CategoryScore {
  categoryKey: string;
  labelI18nKey: string;
  weight: number;
  subScore: number;
  available: boolean;
  evidence: string;
}

export interface SnapshotReport {
  id: string;
  entityType: string;
  entityId: string;
  overallScore: number;
  categories: CategoryScore[];
  modelId: string;
  modelVersion: string;
  generatedAt: string;
}

export interface TaxonomyCategory {
  categoryKey: string;
  weight: number;
  sourcePlane: string;
  labelI18nKey: string;
  sortOrder: number;
  isActive: boolean;
  overridden: boolean;
}

/** Effective signal taxonomy for the tenant (defaults overlaid by overrides). */
export async function getTaxonomy(vertical?: string): Promise<TaxonomyCategory[]> {
  const { data } = await apiClient.get<TaxonomyCategory[]>('/snapshots/taxonomy', {
    params: vertical ? { vertical } : {},
  });
  return data;
}

/** Most recent snapshot for an entity, or null if none has been generated. */
export async function latestSnapshot(
  entityType: SnapshotEntityType,
  entityId: string,
): Promise<SnapshotReport | null> {
  try {
    const { data } = await apiClient.get<SnapshotReport>(`/snapshots/${entityType}/${entityId}/latest`);
    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}

/** Snapshot history for an entity, newest first (the trend). */
export async function listSnapshots(
  entityType: SnapshotEntityType,
  entityId: string,
): Promise<SnapshotReport[]> {
  const { data } = await apiClient.get<SnapshotReport[]>(`/snapshots/${entityType}/${entityId}`);
  return data;
}

/** Download the latest snapshot as a branded PDF (uses the tenant theme). */
export async function downloadSnapshot(
  entityType: SnapshotEntityType,
  entityId: string,
  lang?: string,
): Promise<void> {
  const { data } = await apiClient.get<Blob>(`/snapshots/${entityType}/${entityId}/export`, {
    params: lang ? { lang } : {},
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `snapshot-${entityId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** ModuleRequiredError marks a 403 from the gated generate endpoint. */
export class ModuleRequiredError extends Error {}

/** Generate (and persist) a new snapshot. Throws ModuleRequiredError on 403. */
export async function generateSnapshot(
  entityType: SnapshotEntityType,
  entityId: string,
): Promise<SnapshotReport> {
  try {
    const { data } = await apiClient.post<SnapshotReport>(`/snapshots/${entityType}/${entityId}`, {});
    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 403) {
      throw new ModuleRequiredError('agents module not enabled');
    }
    throw err;
  }
}
